import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Dialog, DialogContent, DialogTitle, Tab, Tabs } from '@mui/material'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import BackHeader from '../../components/BackHeader/BackHeader'
import Notification from '../../components/Notification/Notification'
import Switch from '../../components/Switch/Switch'
import useNotification from '../../hooks/useNotification'
import ScorerList from './ScorerView'
import { MatchFines } from './FineView'
import {
  downloadAppleCalendar,
  hasStarted,
  initialsFrom,
  matchCalendarEvent,
  meetingBeforeKickoff,
  openGoogleCalendar,
  splitMatches,
  toDateTimeLocal,
  toMeetingLocal,
} from './logic'
import { useHold } from './store'
import Loading from '../../components/Loading/Loading'
import TextCard from './components/TextCard'
import {
  ArchiveNote,
  Composer,
  HoldScreen,
  DateTimeField,
  MatchLink,
  SquadNeed,
  People,
  PlaceLink,
  SeasonSwitch,
  SignupButton
} from './bits'

export default function MatchViews({ list = false }) {
  if (list) return <MatchList />
  return <MatchDetail />
}

function MatchList() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = searchParams.get('fane') === 'topscorer' ? 'topscorer' : 'kampe'
  const { team, season, isAdmin, dispatch, loading } = useHold()
  const [open, setOpen] = useState(false)
  const [icalOpen, setIcalOpen] = useState(false)
  const [icalUrl, setIcalUrl] = useState('')
  const [icalNotice, setIcalNotice] = useState('')
  const [icalBusy, setIcalBusy] = useState(false)
  const { showNotification, notificationProps } = useNotification()
  if (loading && !team) return <HoldScreen><Loading fill /></HoldScreen>
  if (!team || !season) return null
  const { upcoming, played } = splitMatches(season.matches)

  const openIcal = () => {
    setIcalUrl(team.icalUrl || '')
    setIcalNotice('')
    setOpen(false)
    setIcalOpen(true)
  }

  const fetchIcal = async (event) => {
    event.preventDefault()
    const link = icalUrl.trim()
    if (!link || icalBusy) return
    setIcalBusy(true)
    setIcalNotice('')
    const saved = await dispatch({
      type: 'SET_TEAM',
      teamId: team.id,
      fields: { icalUrl: link },
    })
    if (!saved.ok) {
      setIcalNotice(saved.error)
      setIcalBusy(false)
      return
    }
    const imported = await dispatch({
      type: 'IMPORT_MATCHES',
      teamId: team.id,
      seasonId: season.id,
    })
    setIcalBusy(false)
    if (!imported.ok) {
      setIcalNotice(imported.error)
      return
    }
    setIcalOpen(false)
  }

  return (
    <HoldScreen>
      <Notification {...notificationProps} />
      <div className="hold-tabbed-top">
        <BackHeader eyebrow={team.name} title="Kampe" subtitle={season.name} onBack={() => navigate(`/hold/${team.id}`)} />
        <div className="hold-tabbed-tabs">
          <Tabs
            value={tab}
            onChange={(_, value) => setSearchParams(value === 'topscorer' ? { fane: 'topscorer' } : {}, { replace: true })}
            variant="fullWidth"
            textColor="inherit"
          >
            <Tab value="kampe" label="Kampe" />
            <Tab value="topscorer" label="Topscorer" />
          </Tabs>
        </div>
      </div>
      <SeasonSwitch team={team} season={season} dispatch={dispatch} />
      {tab === 'topscorer' ? <ScorerList /> : (
      <>
      <ArchiveNote season={season} />
      {isAdmin && season.current ? (
        <div className="hold-match-actions">
          <button type="button" className="hold-action hold-action--ghost" onClick={() => { setIcalOpen(false); setOpen(true) }}>
            Tilføj kamp
          </button>
          <button type="button" className="hold-action hold-action--ghost" onClick={openIcal}>
            iCal fra DBU
          </button>
        </div>
      ) : null}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={goalDialogPaper}
        BackdropProps={goalDialogBackdrop}
      >
        <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>Opret kamp</DialogTitle>
        <DialogContent>
          {open ? (
            <MatchForm
              bare
              teamId={team.id}
              seasonId={season.id}
              dispatch={dispatch}
              onDone={() => setOpen(false)}
              onCreated={(opponent) => showNotification(`Kamp mod ${opponent} er oprettet`, 'success')}
            />
          ) : null}
        </DialogContent>
      </Dialog>
      <Dialog
        open={icalOpen}
        onClose={icalBusy ? undefined : () => setIcalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={goalDialogPaper}
        BackdropProps={goalDialogBackdrop}
      >
        <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>iCal fra DBU</DialogTitle>
        <DialogContent>
          <form className="hold-form hold-create-form" onSubmit={fetchIcal}>
            <label>
              Link
              <input
                value={icalUrl}
                onChange={(event) => setIcalUrl(event.target.value)}
                placeholder="webcal:// eller https://"
                autoFocus
              />
            </label>
            <p className="hold-note">Kampe, du selv har tilføjet, bliver liggende.</p>
            {icalNotice ? <p className="hold-note">{icalNotice}</p> : null}
            <button type="submit" className="hold-action hold-action--save" disabled={!icalUrl.trim() || icalBusy}>
              {icalBusy ? 'Henter…' : 'Hent kampe'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
      <MatchGroup title="Kommende" matches={upcoming} allMatches={season.matches} teamId={team.id} empty="Ingen kommende kampe." />
      <MatchGroup title="Spillet" played matches={played} allMatches={season.matches} teamId={team.id} empty="Ingen spillede kampe." />
      </>
      )}
    </HoldScreen>
  )
}

function MatchGroup({ title, matches, allMatches, teamId, empty, played = false }) {
  return (
    <section className="hold-stack">
      <h2 className={played ? 'is-played' : ''}>{title}</h2>
      {matches.length === 0 ? <p className="hold-empty">{empty}</p> : matches.map((match) => (
        <MatchLink key={match.id} teamId={teamId} match={match} matches={allMatches} />
      ))}
    </section>
  )
}

function MatchForm({ teamId, dispatch, onDone, onCreated, match, seasonId, bare = false }) {
  const editing = Boolean(match)
  const [saving, setSaving] = useState(false)
  const [opponent, setOpponent] = useState(match?.opponent || '')
  const [home, setHome] = useState(match ? match.home : true)
  const [kickoff, setKickoff] = useState(toDateTimeLocal(match?.kickoff))
  const [place, setPlace] = useState(match?.place || '')
  const [max, setMax] = useState(match?.max ?? (editing ? '' : 10))
  const [meeting, setMeeting] = useState(toMeetingLocal(match?.meeting, match?.kickoff))
  const [note, setNote] = useState(match?.note || '')
  const initialMeeting = toMeetingLocal(match?.meeting, match?.kickoff)
  const initialMax = match?.max ?? (editing ? '' : 10)
  const changed = editing && (
    opponent !== (match?.opponent || '')
    || home !== Boolean(match?.home)
    || kickoff !== toDateTimeLocal(match?.kickoff)
    || place !== (match?.place || '')
    || meeting !== initialMeeting
    || note !== (match?.note || '')
    || String(max) !== String(initialMax)
  )
  const ready = Boolean(opponent.trim() && kickoff && place.trim())

  return (
    <form
      className={bare ? 'hold-form hold-create-form' : 'hold-form'}
      onSubmit={async (event) => {
        event.preventDefault()
        if (!opponent.trim() || !kickoff || !place.trim() || saving) return
        const fields = {
          opponent: opponent.trim(),
          home,
          kickoff: new Date(kickoff).toISOString(),
          place: place.trim(),
          meeting: meeting ? new Date(meeting).toISOString() : '',
          note: note.trim(),
          max: max === '' ? null : Number(max),
        }
        if (editing) {
          dispatch({
            type: 'UPDATE_MATCH',
            teamId,
            matchId: match.id,
            match: fields,
          })
          onDone()
          return
        }
        setSaving(true)
        const result = await dispatch({
          type: 'ADD_MATCH',
          teamId,
          seasonId,
          match: fields,
        })
        if (!result?.ok) {
          setSaving(false)
          return
        }
        onCreated?.(fields.opponent)
        onDone()
      }}
    >
      <label>
        <span className="hold-field-name">Modstander<span className="hold-required" aria-hidden="true">*</span></span>
        <input required value={opponent} onChange={(event) => setOpponent(event.target.value)} />
      </label>
      <div className="hold-when">
        <div className="hold-home-field">
          <span>Hjemmekamp</span>
          <span className="hold-home-control">
            <Switch checked={home} onChange={setHome} ariaLabel="Hjemmekamp" color="primary" />
          </span>
        </div>
        <label>
          Max spillere
          <input type="number" min="1" value={max} onChange={(event) => setMax(event.target.value)} placeholder="Intet loft" />
        </label>
      </div>
      <div className="hold-when">
        <label>
          <span className="hold-field-name">Kampstart<span className="hold-required" aria-hidden="true">*</span></span>
          <DateTimeField required value={kickoff} onChange={(event) => setKickoff(event.target.value)} />
        </label>
        <label>
          Mødetid
          <DateTimeField value={meeting} suggest={meetingBeforeKickoff(kickoff)} onChange={(event) => setMeeting(event.target.value)} />
        </label>
      </div>
      <label>
        <span className="hold-field-name">Sted<span className="hold-required" aria-hidden="true">*</span></span>
        <input required value={place} onChange={(event) => setPlace(event.target.value)} />
      </label>
      <label>Note<textarea value={note} onChange={(event) => setNote(event.target.value)} rows={2} /></label>
      {match && !match.manual ? (
        <p className="hold-note">Næste hentning fra DBU opdaterer kampstart, modstander og sted. Mødetid, max og note bliver liggende.</p>
      ) : null}
      <button type="submit" className="hold-action hold-action--save" disabled={saving || (editing ? !changed || !ready : !ready)}>{editing ? 'Gem ændringer' : saving ? 'Gemmer…' : 'Gem kamp'}</button>
    </form>
  )
}

function clockFace(iso) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return { time: '–', day: '' }
  return {
    time: new Intl.DateTimeFormat('da-DK', { hour: '2-digit', minute: '2-digit' }).format(date),
    day: new Intl.DateTimeFormat('da-DK', { weekday: 'short', day: 'numeric', month: 'short' }).format(date),
  }
}

function meetingFace(value, kickoff) {
  if (!value) return { time: '–', day: 'Ikke sat' }
  if (/^\d{2}:\d{2}$/.test(value)) return { time: value, day: 'Samme dag' }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return { time: value, day: '' }
  const start = new Date(kickoff)
  const sameDay = !Number.isNaN(start.getTime()) && date.toDateString() === start.toDateString()
  return {
    time: new Intl.DateTimeFormat('da-DK', { hour: '2-digit', minute: '2-digit' }).format(date),
    day: sameDay
      ? 'Samme dag'
      : new Intl.DateTimeFormat('da-DK', { weekday: 'short', day: 'numeric', month: 'short' }).format(date),
  }
}

function MatchFixture({ match, onAddCalendar }) {
  const kick = clockFace(match.kickoff)
  const meet = meetingFace(match.meeting, match.kickoff)
  return (
    <section className={`hold-fixture${match.status === 'cancelled' ? ' is-cancelled' : ''}${hasStarted(match.kickoff) ? ' is-played' : ''}`}>
      {match.status === 'cancelled' ? <p className="hold-fixture-flag">Aflyst</p> : null}
      <div className="hold-fixture-clocks">
        <div>
          <span>Kampstart</span>
          <strong>{kick.time}</strong>
          <em>{kick.day}</em>
        </div>
        <div className="is-meet">
          <span>Mødetid</span>
          <strong>{meet.time}</strong>
          <em>{meet.day}</em>
        </div>
      </div>
      {match.place ? <p className="hold-fixture-place"><span>{match.place}</span></p> : null}
      {match.place || onAddCalendar ? (
        <div className="hold-fixture-actions">
          {match.place ? <PlaceLink place={match.place} button /> : null}
          {onAddCalendar ? (
            <button type="button" className="hold-fixture-cal" aria-label="Tilføj til kalender" onClick={onAddCalendar}>
              <CalendarMonthOutlinedIcon />
            </button>
          ) : null}
        </div>
      ) : null}
      {match.note ? <p className="hold-fixture-note">{match.note}</p> : null}
      {match.status !== 'cancelled' ? <SquadNeed count={match.attendees.length} max={match.max} /> : null}
    </section>
  )
}

function MatchDetail() {
  const navigate = useNavigate()
  const { matchId } = useParams()
  const { team, isAdmin, dispatch, nameOf, meId, loading } = useHold()
  const [editing, setEditing] = useState(false)
  const [goalOpen, setGoalOpen] = useState(false)
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [declinedOpen, setDeclinedOpen] = useState(false)
  if (loading && !team) return <HoldScreen><Loading fill text="Henter holdet" /></HoldScreen>
  if (!team) return null
  const found = team.seasons
    .map((item) => ({ season: item, match: item.matches.find((entry) => entry.id === matchId) }))
    .find((item) => item.match)
  if (!found) return <HoldScreen><p className="hold-empty">Kampen findes ikke.</p></HoldScreen>
  const { season: matchSeason, match } = found

  const started = hasStarted(match.kickoff)
  const canScore = started && match.status !== 'cancelled'
  const calendarEvent = match.status === 'cancelled' ? null : matchCalendarEvent(team, match)

  return (
    <HoldScreen className={started ? 'is-played-match' : ''}>
      <BackHeader
        eyebrow={team.name}
        title={`${match.home ? 'Hjemme' : 'Ude'} mod ${match.opponent}`}
        subtitle={matchSeason.name}
        onBack={() => navigate(`/hold/${team.id}/kampe`)}
      />
      <MatchFixture
        match={match}
        onAddCalendar={calendarEvent ? () => setCalendarOpen(true) : null}
      />
      <Dialog
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={goalDialogPaper}
        BackdropProps={goalDialogBackdrop}
      >
        <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>Tilføj til kalender</DialogTitle>
        <DialogContent>
          <p className="hold-copy">Vælg hvilken kalender kampen skal lande i.</p>
          <div className="hold-match-actions">
            <button
              type="button"
              className="hold-action"
              onClick={() => {
                downloadAppleCalendar(calendarEvent)
                setCalendarOpen(false)
              }}
            >
              Apple Kalender
            </button>
            <button
              type="button"
              className="hold-action"
              onClick={() => {
                openGoogleCalendar(calendarEvent)
                setCalendarOpen(false)
              }}
            >
              Google Kalender
            </button>
          </div>
        </DialogContent>
      </Dialog>
      {isAdmin ? (
        <button type="button" className="hold-action hold-action--ghost" onClick={() => setEditing((value) => !value)}>
          {editing ? 'Luk' : 'Ret kamp'}
        </button>
      ) : null}
      {editing ? (
        <MatchForm
          teamId={team.id}
          dispatch={dispatch}
          match={match}
          onDone={() => setEditing(false)}
        />
      ) : null}
      <SignupButton
        item={match}
        season={matchSeason}
        onToggle={() => {
          const joined = match.attendees.includes(meId) || match.waitlist.includes(meId)
          const declined = (match.declined || []).includes(meId)
          dispatch({
            type: 'SET_MATCH_REPLY',
            teamId: team.id,
            matchId: match.id,
            reply: joined && !declined ? 'clear' : 'yes',
          })
        }}
        onDecline={() => dispatch({ type: 'SET_MATCH_REPLY', teamId: team.id, matchId: match.id, reply: 'no' })}
      />
      {matchSeason.current && !started && match.status !== 'cancelled' && match.attendees.includes(meId) ? (
        <p className="hold-note">Du får en påmindelse én time før kampstart.</p>
      ) : null}

      <section className="hold-stack">
        <h2>Tilmeldte ({match.attendees.length}{match.max != null ? ` / ${match.max}` : ''})</h2>
        <People ids={match.attendees} nameOf={nameOf} empty="Ingen er meldt til endnu." />
        <h2>Venteliste</h2>
        <People ids={match.waitlist} nameOf={nameOf} empty="Ingen på venteliste." />
      </section>
      <button type="button" className="hold-action hold-action--ghost" onClick={() => setDeclinedOpen(true)}>
        Se afbud ({(match.declined || []).length})
      </button>
      <Dialog
        open={declinedOpen}
        onClose={() => setDeclinedOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ ...goalDialogPaper, className: 'hold-create-paper hold-notice-paper' }}
        BackdropProps={goalDialogBackdrop}
      >
        <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>Afbud</DialogTitle>
        <DialogContent>
          {(match.declined || []).length === 0 ? <p className="hold-empty">Ingen har meldt afbud.</p> : (
            <ul className="hold-event-roster">
              {(match.declined || []).map((id) => {
                const name = nameOf(id)
                return (
                  <li key={id}>
                    <span className="hold-fine-avatar" aria-hidden="true">{initialsFrom(name)}</span>
                    <strong>{name}</strong>
                  </li>
                )
              })}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      <MatchFines team={team} season={matchSeason} match={match} />

      {canScore ? (
        <section className="hold-stack">
          <h2>Mål</h2>
          {match.goals.length === 0 ? <p className="hold-empty">Ingen mål registreret.</p> : (
            <ul className="hold-score-list">
              {match.goals.map((goal) => (
                <li key={goal.playerId}>
                  <span>{nameOf(goal.playerId)}</span>
                  <span className="hold-stepper">
                    <button type="button" onClick={() => dispatch({ type: 'SET_GOAL', teamId: team.id, seasonId: matchSeason.id, matchId: match.id, playerId: goal.playerId, count: goal.count - 1 })}>-</button>
                    <span>{goal.count}</span>
                    <button type="button" onClick={() => dispatch({ type: 'SET_GOAL', teamId: team.id, seasonId: matchSeason.id, matchId: match.id, playerId: goal.playerId, count: goal.count + 1 })}>+</button>
                  </span>
                </li>
              ))}
            </ul>
          )}
          <button type="button" className="hold-action hold-action--ghost" onClick={() => setGoalOpen(true)}>
            Tilføj målscorer
          </button>
          <GoalModal
            open={goalOpen}
            team={team}
            season={matchSeason}
            match={match}
            nameOf={nameOf}
            dispatch={dispatch}
            onClose={() => setGoalOpen(false)}
          />
        </section>
      ) : null}

      <section className="hold-stack">
        <h2>Kampens væg</h2>
        {match.messages.length === 0 ? <p className="hold-empty">Ingen beskeder endnu.</p> : match.messages.map((message) => (
          <div key={message.id} className="hold-stack">
            <TextCard
              text={message.text}
              image={message.image}
              allowImage
              meta={message.authorName || nameOf(message.authorId)}
              at={message.createdAt}
              canEdit={message.authorId === meId}
              mine={message.authorId === meId}
              like={{
                count: (message.likedBy || []).length,
                people: [...(message.likedBy || [])]
                  .map((id) => ({ id, name: nameOf(id) }))
                  .sort((a, b) => a.name.localeCompare(b.name, 'da')),
                on: (message.likedBy || []).includes(meId),
                onToggle: message.authorId === meId
                  ? null
                  : () => dispatch({ type: 'TOGGLE_MATCH_LIKE', teamId: team.id, messageId: message.id }),
              }}
              onSave={(text, image) => dispatch({
                type: 'UPDATE_MATCH_MESSAGE',
                teamId: team.id,
                seasonId: matchSeason.id,
                matchId: match.id,
                messageId: message.id,
                text,
                image,
              })}
              onDelete={() => dispatch({
                type: 'DELETE_MATCH_MESSAGE',
                teamId: team.id,
                seasonId: matchSeason.id,
                matchId: match.id,
                messageId: message.id,
              })}
              canComment={true}
              onCommenting={(text) => dispatch({ type: 'ADD_MATCH_COMMENT', teamId: team.id, messageId: message.id, text })}
            />
            <div className="hold-comments">
              {(message.comments || []).map((comment) => (
                <TextCard
                  key={comment.id}
                  compact
                  text={comment.text}
                  meta={comment.authorName || nameOf(comment.authorId)}
                  at={comment.createdAt}
                  canEdit={comment.authorId === meId}
                  mine={comment.authorId === meId}
                  onSave={(text) => dispatch({ type: 'UPDATE_COMMENT', teamId: team.id, commentId: comment.id, text })}
                  onDelete={() => dispatch({ type: 'DELETE_COMMENT', teamId: team.id, commentId: comment.id })}
                />
              ))}
            </div>
          </div>
        ))}
        <Composer
          allowImage
          placeholder="Skriv på kampens væg"
          onSubmit={(text, image) => dispatch({
            type: 'ADD_MATCH_MESSAGE',
            teamId: team.id,
            seasonId: matchSeason.id,
            matchId: match.id,
            text,
            image,
          })}
        />
      </section>
    </HoldScreen>
  )
}

const goalDialogPaper = {
  className: 'hold-create-paper',
  sx: {
    backgroundColor: '#221c24',
    color: '#f3eadf',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: '18px',
    boxShadow: '0 24px 60px rgba(0, 0, 0, 0.55)',
  },
}

const goalDialogBackdrop = {
  sx: {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
}

function GoalModal({ open, team, season, match, nameOf, dispatch, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={goalDialogPaper}
      BackdropProps={goalDialogBackdrop}
    >
      <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>Tilføj målscorer</DialogTitle>
      <DialogContent>
        {open ? (
          <GoalForm
            team={team}
            season={season}
            match={match}
            nameOf={nameOf}
            dispatch={dispatch}
            onDone={onClose}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

function GoalForm({ team, season, match, nameOf, dispatch, onDone }) {
  const [showAll, setShowAll] = useState(false)
  const [counts, setCounts] = useState({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const signedUp = new Set(match.attendees)
  const already = new Set(match.goals.map((goal) => goal.playerId))
  const members = (showAll ? team.members.map((member) => member.id) : match.attendees)
    .filter((id) => !already.has(id))
    .sort((a, b) => nameOf(a).localeCompare(nameOf(b), 'da'))
  const chosen = members.filter((id) => counts[id] > 0)
  const total = chosen.reduce((sum, id) => sum + counts[id], 0)

  const toggle = (id) => {
    setCounts((current) => {
      const next = { ...current }
      if (next[id]) delete next[id]
      else next[id] = 1
      return next
    })
  }
  const setCount = (id, count) => {
    setCounts((current) => {
      const next = { ...current }
      if (count < 1) delete next[id]
      else next[id] = count
      return next
    })
  }
  const pickAll = () => {
    const allOn = members.every((id) => counts[id] > 0)
    setCounts((current) => {
      const next = { ...current }
      members.forEach((id) => {
        if (allOn) delete next[id]
        else if (!next[id]) next[id] = 1
      })
      return next
    })
  }
  const showTeam = (next) => {
    setShowAll(next)
    if (!next) {
      setCounts((current) => {
        const kept = {}
        Object.entries(current).forEach(([id, count]) => {
          if (signedUp.has(id)) kept[id] = count
        })
        return kept
      })
    }
  }
  const add = async () => {
    if (chosen.length === 0 || busy) return
    setBusy(true)
    setError('')
    for (const playerId of chosen) {
      const result = await dispatch({
        type: 'SET_GOAL',
        teamId: team.id,
        seasonId: season.id,
        matchId: match.id,
        playerId,
        count: counts[playerId],
      })
      if (!result?.ok) {
        setError(result?.error || 'Målet kunne ikke gemmes')
        setBusy(false)
        return
      }
      setCounts((current) => {
        const next = { ...current }
        delete next[playerId]
        return next
      })
    }
    setBusy(false)
    onDone()
  }
  const label = chosen.length === 0
    ? 'Vælg spillere'
    : chosen.length === 1
      ? `Tilføj ${total} mål til ${nameOf(chosen[0])}`
      : `Tilføj ${total} mål`

  return (
    <div className="hold-form hold-create-form">
      <p className="hold-copy">Vælg spillere og angiv, hvor mange mål de har scoret.</p>
      <div className="hold-give-people-head">
        <button type="button" className="hold-text-button" onClick={() => showTeam(!showAll)}>
          {showAll ? 'Kun tilmeldte' : 'Vis hele holdet'}
        </button>
        {members.length > 1 ? (
          <button type="button" className="hold-text-button" onClick={pickAll}>
            {members.every((id) => counts[id] > 0) ? 'Fravælg alle' : 'Vælg alle'}
          </button>
        ) : <span />}
      </div>
      {members.length === 0 ? (
        <p className="hold-empty">{showAll ? 'Alle på holdet har allerede mål.' : 'Ingen tilmeldte uden mål.'}</p>
      ) : (
        <ul className="hold-give-list">
          {members.map((id) => {
            const count = counts[id] || 0
            const person = nameOf(id)
            return (
              <li key={id}>
                <div className={`hold-give-pick hold-goal-row ${count > 0 ? 'is-on' : ''}`}>
                  <button type="button" className="hold-goal-person" aria-pressed={count > 0} onClick={() => toggle(id)}>
                    <span className="hold-fine-avatar hold-give-avatar" aria-hidden="true">{initialsFrom(person)}</span>
                    <span className="hold-give-name">
                      {person}
                      {showAll && !signedUp.has(id) ? <em>Ikke tilmeldt</em> : null}
                    </span>
                  </button>
                  {count > 0 ? (
                    <span className="hold-stepper">
                      <button type="button" aria-label="Færre mål" disabled={busy} onClick={() => setCount(id, count - 1)}>-</button>
                      <span>{count}</span>
                      <button type="button" aria-label="Flere mål" disabled={busy} onClick={() => setCount(id, count + 1)}>+</button>
                    </span>
                  ) : null}
                </div>
              </li>
            )
          })}
        </ul>
      )}
      {error ? <p className="hold-note">{error}</p> : null}
      <button type="button" className="hold-action hold-action--save" disabled={chosen.length === 0 || busy} onClick={add}>
        {busy ? 'Tilføjer…' : label}
      </button>
    </div>
  )
}
